import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import AuthContext from "../Context/AuthProvider";
import { getVisits } from "../API/VisitsApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid2,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  CalendarMonth,
  Refresh,
  CheckCircleOutline,
  PeopleOutline,
  LocalHospital,
  Recommend,
  MedicalServices,
  TrendingUp,
  EventBusy,
  MedicalInformation,
  Visibility,
  Download,
  Close,
  FiberNew,
  HourglassTop,
  Cancel,
} from "@mui/icons-material";
import dayjs from "dayjs";
import DateRangeDialog from "./ChangeDates";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import { getDisplayValue } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import { useTheme, useMediaQuery } from "@mui/material";
import { exportPDF } from "../Utility/PdfConverterForReport";
import Chart from "chart.js/auto";

// ─── Chart.js global instances ───────────────────────────────────────────────
let chartInstances = {};

// ─── Colors ──────────────────────────────────────────────────────────────────
const C = {
  teal: "#1D9E75",
  tealL: "#E1F5EE",
  coral: "#D85A30",
  coralL: "#FAECE7",
  blue: "#378ADD",
  blueL: "#E6F1FB",
  amber: "#BA7517",
  amberL: "#FAEEDA",
  purple: "#7F77DD",
  purpleL: "#EEEDFE",
  green: "#639922",
  greenL: "#EAF3DE",
  pink: "#D4537E",
  pinkL: "#FBEAF0",
};

// ─── CSV Download ─────────────────────────────────────────────────────────────
function downloadCSV(rows, columns, filename) {
  const header = columns.map((c) => c.headerName).join(",");
  const body = rows.map((row) =>
    columns
      .map((c) => `"${String(row[c.field] ?? "").replace(/"/g, '""')}"`)
      .join(","),
  );
  const blob = new Blob([[header, ...body].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

// ─── Table Dialog ─────────────────────────────────────────────────────────────
// Replace the TableDialog function entirely
function TableDialog({ open, onClose, title, rows, columns, csvFilename, auth, filters }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDownloadPdf = () => {
    exportPDF(
      rows,
      columns,
      filters?.from?.toISOString() || null,
      filters?.to?.toISOString() || null,
      title,
      auth?.selectedFacility?.logo
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 0,
          px: 0,
        }}
      >
        <MenuBar showDownload={true} onDownload={handleDownloadPdf} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0, height: 460 }}>
        {isMobile ? (
          <Box sx={{ px: 1, py: 1, maxHeight: 460, overflowY: "auto" }}>
            {rows.map((row, idx) => (
              <Card key={row.id || idx} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
                  {columns.map((col) =>
                    row[col.field] !== undefined && row[col.field] !== null && row[col.field] !== "" ? (
                      <Box key={col.field} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}
                          sx={{ minWidth: 90, fontSize: "0.7rem" }}>
                          {col.headerName}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.72rem", textAlign: "right", flex: 1 }}>
                          {String(row[col.field])}
                        </Typography>
                      </Box>
                    ) : null
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              density: "compact",
              pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[10, 20, 50]}
            sx={{
              border: "none",
              height: 460,
              "& .MuiTablePagination-toolbar": { minHeight: "unset", alignItems: "center" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { margin: 0 },
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Stat Card — fixed layout, no overlap ────────────────────────────────────
function StatCard({ icon, label, value, color, bgColor, subtitle, onView }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: `1.5px solid ${bgColor}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          padding: "12px 14px !important",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: 0.5,
        }}
      >
        {/* Icon + Label row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              background: bgColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 15, color } })}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            sx={{ lineHeight: 1.2, fontSize: "0.68rem" }}
          >
            {label}
          </Typography>
        </Box>

        {/* Value */}
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ color, lineHeight: 1, mt: 0.5 }}
        >
          {value}
        </Typography>

        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.62rem", lineHeight: 1.3 }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Spacer pushes button to bottom */}
        <Box sx={{ flex: 1 }} />

        {/* VIEW button — always at bottom, never overlaps */}
        {onView && (
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<Visibility sx={{ fontSize: "11px !important" }} />}
              onClick={onView}
              sx={{
                fontSize: "0.62rem",
                py: 0.25,
                px: 1,
                minHeight: 0,
                lineHeight: 1.4,
                background: color,
                "&:hover": { background: color, opacity: 0.88 },
                borderRadius: 1,
              }}
            >
              VIEW
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ title, icon, color, children, onViewAll }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {icon && React.cloneElement(icon, { sx: { fontSize: 14, color } })}
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                fontSize: "0.68rem",
                letterSpacing: 0.5,
              }}
            >
              {title}
            </Typography>
          </Box>
        }
        action={
          onViewAll && (
            <Button
              size="small"
              startIcon={<Visibility sx={{ fontSize: 12 }} />}
              onClick={onViewAll}
              sx={{ fontSize: "0.68rem", py: 0.2, color }}
            >
              View All
            </Button>
          )
        }
        sx={{ pb: 0, pt: 1.5, px: 2 }}
      />
      <CardContent sx={{ pt: 1, pb: "12px !important" }}>
        {children}
      </CardContent>
    </Card>
  );
}

// ─── Leader List ──────────────────────────────────────────────────────────────
function LeaderList({ data, colors }) {
  if (!data || data.length === 0)
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        py={2}
      >
        No data
      </Typography>
    );
  const max = data[0][1];
  return data.map(([name, count], i) => (
    <Box key={name} sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
        <Typography
          variant="caption"
          fontWeight={i === 0 ? 600 : 400}
          sx={{
            maxWidth: "82%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {i + 1}. {name}
        </Typography>
        <Chip
          label={count}
          size="small"
          sx={{
            height: 18,
            fontSize: "0.62rem",
            background: colors[i % colors.length] + "22",
            color: colors[i % colors.length],
            fontWeight: 700,
          }}
        />
      </Box>
      <Box sx={{ height: 5, background: "#ebebeb", borderRadius: 99 }}>
        <Box
          sx={{
            width: `${Math.round((count / max) * 100)}%`,
            height: "100%",
            background: colors[i % colors.length],
            borderRadius: 99,
            transition: "width 0.5s ease",
          }}
        />
      </Box>
    </Box>
  ));
}

// ─── Canvas wrapper ───────────────────────────────────────────────────────────
function ChartBox({ id, height = 180, label }) {
  return (
    <Box sx={{ position: "relative", width: "100%", height }}>
      <canvas id={id} role="img" aria-label={label}>
        {label}
      </canvas>
    </Box>
  );
}

// ─── Visit table columns ──────────────────────────────────────────────────────
const makeVisitCols = (auth) => [
  { field: "displayId", headerName: "Visit ID", width: 120 },
  {
    field: "patient",
    headerName: "Patient",
    width: 180,
    valueGetter: (_, r) =>
      `${r.patientPrefix || ""} ${r.patientFirstName || ""} ${r.patientLastName || ""}`.trim(),
  },
  { field: "patientMobileNo", headerName: "Mobile", width: 130 },
  {
    field: "physician",
    headerName: "Doctor",
    width: 180,
    valueGetter: (_, r) =>
      `${r.physicianPrefix || ""} ${r.physicianFirstName || ""} ${r.physicianLastName || ""}`.trim(),
  },
  { field: "primaryServiceName", headerName: "Service", width: 160 },
  {
    field: "visitStatus",
    headerName: "Status",
    width: 120,
    valueGetter: (_, r) => getDisplayValue(r.visitStatus),
  },
  {
    field: "scheduledStartDateTime",
    headerName: "Scheduled",
    width: 180,
    valueGetter: (_, r) =>
      convertUTCToTimeZoneDateTimeString(
        r.scheduledStartDateTime,
        auth?.selectedFacility?.timeZone,
      ),
  },
  {
    field: "referredBy",
    headerName: "Referred By",
    width: 150,
    valueGetter: (_, r) => r.referredBy || "Self / Walk-in",
  },
];

const doctorCols = [
  { field: "id", headerName: "#", width: 60 },
  { field: "name", headerName: "Doctor", width: 240 },
  { field: "count", headerName: "Total Visits", width: 130 },
];

const serviceCols = [
  { field: "id", headerName: "#", width: 60 },
  { field: "name", headerName: "Service", width: 300 },
  { field: "count", headerName: "Total Visits", width: 130 },
];

// ════════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
export default function VisitDashboard() {
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDateDlg, setOpenDateDlg] = useState(false);
  // const [chartReady, setChartReady] = useState(false);
  const [groupBy, setGroupBy] = useState("daily");
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    rows: [],
    columns: [],
    csv: "data.csv",
  });

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const [filters, setFilters] = useState({
    from: dayjs().subtract(30, "day"),
    to: dayjs(),
    selectedDateRangeOption: "Last30Days",
    statusList: [
      "Scheduled",
      "CheckedIn",
      "InProgress",
      "Cancelled",
      "Completed",
      "NoShow",
    ],
  });

  // Load Chart.js from CDN
  // useEffect(() => {
  //   if (window.Chart) {
  //     setChartReady(true);
  //     return;
  //   }
  //   const s = document.createElement("script");
  //   s.src =
  //     "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
  //   s.onload = () => setChartReady(true);
  //   document.head.appendChild(s);
  // }, []);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    const result = await getVisits(auth, filters);
    if (result.isSuccess) setVisits(result.data || []);
    else showSnackbar("Failed to load visit data", "error");
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, filters]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  // ── Metrics ───────────────────────────────────────────────────────────────
  const m = useMemo(() => {
    const total = visits.length;
    const newV = visits.filter((v) => v.visitStatus === "Scheduled");
    const openV = visits.filter((v) =>
      ["CheckedIn", "InProgress"].includes(v.visitStatus),
    );
    const completedV = visits.filter((v) => v.visitStatus === "Completed");
    const cancelledV = visits.filter((v) => v.visitStatus === "Cancelled");
    const noShowV = visits.filter((v) => v.visitStatus === "NoShow");
    const closedV = [...completedV, ...cancelledV, ...noShowV];
    const referredV = visits.filter((v) => v.referredBy?.trim());
    const selfV = visits.filter((v) => !v.referredBy?.trim());

    // Doctor aggregation
    const doctorMap = {};
    visits.forEach((v) => {
      const name =
        `${v.physicianPrefix || ""} ${v.physicianFirstName || ""} ${v.physicianLastName || ""}`.trim();
      if (name) doctorMap[name] = (doctorMap[name] || 0) + 1;
    });
    const doctorData = Object.entries(doctorMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
    const doctorTableRows = Object.entries(doctorMap)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count], i) => ({ id: i + 1, name, count }));

    // Service aggregation
    const serviceMap = {};
    visits.forEach((v) => {
      const svc = v.primaryServiceName || "Unknown";
      serviceMap[svc] = (serviceMap[svc] || 0) + 1;
    });
    const serviceData = Object.entries(serviceMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
    const serviceTableRows = Object.entries(serviceMap)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count], i) => ({ id: i + 1, name, count }));

    // Avg / day
    const days = Math.max(filters.to.diff(filters.from, "day") + 1, 1);
    const avgPerDay = (total / days).toFixed(1);

    // Trend
    const dailyMap = {};
    visits.forEach((v) => {
      const d = v.scheduledStartDateTime
        ? dayjs(v.scheduledStartDateTime).format("DD MMM")
        : null;
      if (d) dailyMap[d] = (dailyMap[d] || 0) + 1;
    });

  const trendLabels = [], trendValues = [], trendDateGroups = {};

    // if (groupBy === "hourly") {
    //   const hourMap = {};
    //   visits.forEach((v) => {
    //     if (v.scheduledStartDateTime) {
    //       const h = dayjs(v.scheduledStartDateTime).format("HH:00");
    //       hourMap[h] = (hourMap[h] || 0) + 1;
    //     }
    //   });
    //   for (let h = 0; h < 24; h++) {
    //     const lbl = `${String(h).padStart(2, "0")}:00`;
    //     trendLabels.push(lbl);
    //     trendValues.push(hourMap[lbl] || 0);
    //     trendDateGroups[lbl] = visits.filter(
    //       (v) => v.scheduledStartDateTime && dayjs(v.scheduledStartDateTime).format("HH:00") === lbl
    //     );
    //   }
    // } 
     if (groupBy === "daily") {
      let cur = filters.from.clone();
      while (cur.isBefore(filters.to) || cur.isSame(filters.to, "day")) {
        const lbl = cur.format("DD MMM");
        trendLabels.push(lbl);
        trendValues.push(dailyMap[lbl] || 0);
        trendDateGroups[lbl] = visits.filter(
          (v) => v.scheduledStartDateTime && dayjs(v.scheduledStartDateTime).format("DD MMM") === lbl
        );
        cur = cur.add(1, "day");
      }
    } else if (groupBy === "weekly") {
      let ws = filters.from.clone().startOf("week");
      while (ws.isBefore(filters.to)) {
        const we = ws.add(6, "day");
        const weekVisits = visits.filter((v) => {
          if (!v.scheduledStartDateTime) return false;
          const d = dayjs(v.scheduledStartDateTime);
          return (d.isAfter(ws) || d.isSame(ws, "day")) && (d.isBefore(we) || d.isSame(we, "day"));
        });
        const lbl = `${ws.format("DD MMM")} - ${we.format("DD MMM")}`;
        trendLabels.push(lbl);
        trendValues.push(weekVisits.length);
        trendDateGroups[lbl] = weekVisits;
        ws = ws.add(7, "day");
      }
    } else if (groupBy === "monthly") {
      let ms = filters.from.clone().startOf("month");
      while (ms.isBefore(filters.to)) {
        const lbl = ms.format("MMM YYYY");
        const monthVisits = visits.filter(
          (v) => v.scheduledStartDateTime && dayjs(v.scheduledStartDateTime).format("MMM YYYY") === lbl
        );
        trendLabels.push(lbl);
        trendValues.push(monthVisits.length);
        trendDateGroups[lbl] = monthVisits;
        ms = ms.add(1, "month");
      }
    }

    return {
      total,
      newV,
      openV,
      completedV,
      cancelledV,
      noShowV,
      closedV,
      referredV,
      selfV,
      avgPerDay,
      days,
      doctorData,
      doctorTableRows,
      serviceData,
      serviceTableRows,
      trendLabels,
      trendValues,
       trendDateGroups,
    };
  }, [visits, filters, groupBy]);

  const visitCols = useMemo(() => makeVisitCols(auth), [auth]);

  const openVisitDlg = (title, rows, csv) =>
    setDialog({
      open: true,
      title,
      rows: rows.map((v, i) => ({ ...v, id: v.id || i })),
      columns: visitCols,
      csv,
    });

 const openVisitDlgRef = React.useRef(openVisitDlg);
  useEffect(() => { openVisitDlgRef.current = openVisitDlg; });
  const trendDateGroupsRef = React.useRef(m.trendDateGroups);
  useEffect(() => { trendDateGroupsRef.current = m.trendDateGroups; });
  const mRef = React.useRef(m);
  useEffect(() => { mRef.current = m; });

  // ── Charts ────────────────────────────────────────────────────────────────
  useEffect(() => {
    // if (!chartReady || loading) return;
    // const Chart = window.Chart;
    // if (!Chart) return;
    if (loading) return;

    const destroy = (id) => {
      if (chartInstances[id]) {
        chartInstances[id].destroy();
        delete chartInstances[id];
      }
    };

    // Trend bar
    // Trend — line chart with traffic-color points & segments (same as worklist hourly)
   const tEl = document.getElementById("vd-trend");
    if (tEl && m.trendLabels.length > 0) {
      destroy("trend");
      const maxVal = Math.max(...m.trendValues, 1);
      chartInstances["trend"] = new Chart(tEl, {
        type: "bar",
        data: {
          labels: m.trendLabels,
          datasets: [
            {
              label: "Visits",
              data: m.trendValues,
              backgroundColor: m.trendValues.map((v) => {
                const r = v / maxVal;
                return r >= 0.75 ? "#D32F2F" + "CC" : r >= 0.4 ? "#F9A825" + "CC" : C.green + "CC";
              }),
              borderColor: m.trendValues.map((v) => {
                const r = v / maxVal;
                return r >= 0.75 ? "#D32F2F" : r >= 0.4 ? "#F9A825" : C.green;
              }),
              borderWidth: 1,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const v = ctx.raw;
                  const r = v / maxVal;
                  const traffic = r >= 0.75 ? "🔴 High" : r >= 0.4 ? "🟡 Medium" : "🟢 Low";
                  return ` ${v} visits  ${traffic}`;
                },
              },
            },
          },
          scales: {
            x: {
              ticks: { font: { size: 9 }, maxRotation: 45, autoSkip: false, maxTicksLimit: undefined },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { font: { size: 10 }, stepSize: 1 },
              grid: { color: "rgba(0,0,0,0.04)" },
            },
          },
          onClick: (evt, elements) => {
            if (!elements.length) return;
            const idx = elements[0].index;
            const lbl = m.trendLabels[idx];
            const rows = trendDateGroupsRef.current[lbl] || [];
            if (rows.length > 0) {
              openVisitDlgRef.current(`Visits — ${lbl}`, rows, `visits_${lbl}.csv`);
            }
          },
        },
      });
    }

    // Doctor horizontal bar
    const dEl = document.getElementById("vd-doctor");
    if (dEl && m.doctorData.length > 0) {
      destroy("doctor");
      chartInstances["doctor"] = new Chart(dEl, {
        type: "bar",
        data: {
          labels: m.doctorData.map(([n]) =>
            n.length > 22 ? n.slice(0, 20) + "…" : n,
          ),
          datasets: [
            {
              data: m.doctorData.map(([, v]) => v),
              backgroundColor: [
                C.purple + "CC",
                C.blue + "CC",
                C.teal + "CC",
                C.coral + "CC",
                C.amber + "CC",
                C.pink + "CC",
              ],
              borderRadius: 4,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              beginAtZero: true,
              ticks: { font: { size: 9 }, stepSize: 1 },
              grid: { color: "rgba(0,0,0,0.04)" },
            },
            y: { ticks: { font: { size: 10 } }, grid: { display: false } },
          },
        },
      });
    }

    // Service doughnut
    const sEl = document.getElementById("vd-service");
    if (sEl && m.serviceData.length > 0) {
      destroy("service");
      chartInstances["service"] = new Chart(sEl, {
        type: "doughnut",
        data: {
          labels: m.serviceData.map(([n]) => n),
          datasets: [
            {
              data: m.serviceData.map(([, v]) => v),
              backgroundColor: [
                C.teal,
                C.purple,
                C.blue,
                C.coral,
                C.amber,
                C.pink,
              ],
              borderWidth: 2,
              borderColor: "#fff",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "right",
              labels: { font: { size: 10 }, padding: 8, boxWidth: 10 },
            },
          },
          cutout: "58%",
        },
      });
    }

    // Referral doughnut
    const rEl = document.getElementById("vd-referral");
    if (rEl && m.total > 0) {
      destroy("referral");
      chartInstances["referral"] = new Chart(rEl, {
        type: "doughnut",
        data: {
          labels: ["Self / Walk-in", "Referred"],
          datasets: [
            {
              data: [m.selfV.length, m.referredV.length],
              backgroundColor: [C.blue, C.coral],
              borderWidth: 2,
              borderColor: "#fff",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: 10 }, padding: 6, boxWidth: 10 },
            },
          },
          cutout: "55%",
        },
      });
    }

    // Status doughnut
    // Status doughnut — clickable
    const stEl = document.getElementById("vd-status");
    if (stEl && m.total > 0) {
      destroy("status");
      const statusEntries = [
        ["Completed", m.completedV.length, C.green, m.completedV],
        ["Open", m.openV.length, C.blue, m.openV],
        ["New", m.newV.length, C.teal, m.newV],
        ["Cancelled", m.cancelledV.length, C.coral, m.cancelledV],
        ["No Show", m.noShowV.length, C.amber, m.noShowV],
      ].filter(([, v]) => v > 0);
      chartInstances["status"] = new Chart(stEl, {
        type: "doughnut",
        data: {
          labels: statusEntries.map(([k]) => k),
          datasets: [
            {
              data: statusEntries.map(([, v]) => v),
              backgroundColor: statusEntries.map(([, , c]) => c),
              borderWidth: 2,
              borderColor: "#fff",
              hoverOffset: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: 10 }, padding: 6, boxWidth: 10 },
            },
          },
          cutout: "55%",
          onClick: (evt, elements) => {
            if (!elements.length) return;
            const [label, , , rows] = statusEntries[elements[0].index];
            openVisitDlg(
              `${label} Visits`,
              rows,
              `${label.toLowerCase().replace(" ", "_")}_visits.csv`,
            );
          },
        },
      });
    }

    // Closed-3 pie — Completed + Cancelled + NoShow
   const c3El = document.getElementById("vd-closed3");
    const c3Total = m.completedV.length + m.cancelledV.length + m.noShowV.length;
    if (c3El && c3Total > 0) {
      destroy("closed3");
      const c3Entries = [
        ["Completed", m.completedV.length, C.green, m.completedV, "completed_visits.csv"],
        ["Cancelled",  m.cancelledV.length, C.coral, m.cancelledV, "cancelled_visits.csv"],
        ["No Show",    m.noShowV.length,    C.amber, m.noShowV,    "noshow_visits.csv"],
      ].filter(([, v]) => v > 0);
      chartInstances["closed3"] = new Chart(c3El, {
        type: "pie",
        data: {
          labels: c3Entries.map(([k]) => k),
          datasets: [{
            data: c3Entries.map(([, v]) => v),
            backgroundColor: c3Entries.map(([, , c]) => c + "CC"),
            borderColor: c3Entries.map(([, , c]) => c),
            borderWidth: 2,
            hoverOffset: 6,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "bottom", labels: { font: { size: 10 }, padding: 6, boxWidth: 10 } },
          },
          onClick: (evt, elements) => {
            if (!elements.length) return;
            const [label, , , rows, csv] = c3Entries[elements[0].index];
            openVisitDlgRef.current(`${label} Visits`, rows, csv);
          },
        },
      });
    }

    return () => Object.keys(chartInstances).forEach(destroy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m, loading]);

  const dayCount = filters.to.diff(filters.from, "day") + 1;
  const docColors = [C.purple, C.blue, C.teal, C.coral, C.amber, C.pink];
  const svcColors = [C.teal, C.purple, C.blue, C.coral, C.amber, C.pink];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        maxHeight: "calc(100vh - 100px)",
        overflowY: "auto",
        background: "#f7f8fa",
      }}
    >
      {/* ── MenuBar-style header — matches AppointmentListNew1 ── */}
      <Box sx={{ background: "#fff" }}>
        <MenuBar
          showChangeDate={true}
          onChangeDate={() => setOpenDateDlg(true)}
          showRefresh={true}
          onRefresh={fetchVisits}
          refreshDisabled={loading}
          disabled={loading}
        />
        <Box sx={{ px: 1, pb: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            Showing data from{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {filters.from.format("DD/MM/YYYY")}
            </Box>{" "}
            to{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {filters.to.format("DD/MM/YYYY")}
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: C.teal }} />
          </Box>
        )}

        {!loading && (
          <>
            {/* ── Row 1: Stat Cards ── */}
            <Grid2 container spacing={1.5} sx={{ mb: 2 }}>
              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard
                  icon={<PeopleOutline />}
                  label="Total Visits"
                  value={m.total}
                  color={C.teal}
                  bgColor={C.tealL}
                  onView={() =>
                    openVisitDlg("All Visits", visits, "all_visits.csv")
                  }
                />
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard
                  icon={<FiberNew />}
                  label="New (Scheduled)"
                  value={m.newV.length}
                  color={C.blue}
                  bgColor={C.blueL}
                  subtitle="Awaiting check-in"
                  onView={() =>
                    openVisitDlg(
                      "New / Scheduled Visits",
                      m.newV,
                      "new_visits.csv",
                    )
                  }
                />
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard
                  icon={<HourglassTop />}
                  label="Open / Active"
                  value={m.openV.length}
                  color={C.purple}
                  bgColor={C.purpleL}
                  subtitle="CheckedIn + InProgress"
                  onView={() =>
                    openVisitDlg(
                      "Open / Active Visits",
                      m.openV,
                      "open_visits.csv",
                    )
                  }
                />
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard
                  icon={<CheckCircleOutline />}
                  label="Closed"
                  value={m.closedV.length}
                  color={C.green}
                  bgColor={C.greenL}
                  subtitle="Completed + Cancelled + NoShow"
                  onView={() =>
                    openVisitDlg(
                      "Closed Visits",
                      m.closedV,
                      "closed_visits.csv",
                    )
                  }
                />
              </Grid2>

              {/* <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard
                  icon={<EventBusy />}
                  label="No Show"
                  value={m.noShowV.length}
                  color={C.amber}
                  bgColor={C.amberL}
                  onView={() => openVisitDlg("No Show Visits", m.noShowV, "noshow_visits.csv")}
                />
              </Grid2> */}

              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard
                  icon={<TrendingUp />}
                  label="Avg / Day"
                  value={m.avgPerDay}
                  color={C.coral}
                  bgColor={C.coralL}
                  subtitle={`Over ${dayCount} days`}
                />
              </Grid2>
            </Grid2>

           {/* ── Row 2: Trend ── */}
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
                      Visit Trend
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {[["#D32F2F", "High"], ["#F9A825", "Medium"], ["#1D9E75", "Low"]].map(([bg, label]) => (
                        <React.Fragment key={label}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: bg, ml: label !== "High" ? 0.5 : 0 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>{label}</Typography>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                }
                action={
                  <Box sx={{ pr: 1, pt: 0.5 }}>
                    <select
                      value={groupBy}
                      onChange={(e) => setGroupBy(e.target.value)}
                      style={{
                        fontSize: "0.72rem",
                        border: "1px solid #e0e0e0",
                        borderRadius: 6,
                        padding: "3px 8px",
                        background: "#fff",
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </Box>
                }
                sx={{ pb: 0, pt: 1.5, px: 2 }}
              />
              <CardContent sx={{ pt: 1, pb: "12px !important" }}>
                {m.trendLabels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                    No scheduled time data available
                  </Typography>
                ) : (
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box sx={{
                      position: "relative", height: 180,
                      minWidth: groupBy === "daily" && m.trendLabels.length > 14
                        ? m.trendLabels.length * 35
                        : groupBy === "weekly" && m.trendLabels.length > 8
                          ? m.trendLabels.length * 80
                          : undefined,
                    }}>
                      <canvas id="vd-trend" role="img" aria-label="Visit trend chart" />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* ── Row 3: Doctor | Service | Referral+Status ── */}
            {/* ── Row 3: Doctor | Service | Referral + Status + Closed3 ── */}
            {/* ── Row 3a: Doctor | Service | Referral ── */}
<Grid2 container spacing={1.5} sx={{ mb: 1.5 }}>

  <Grid2 size={{ xs: 12, md: 5 }}>
    <SectionCard
      title="Visits by Doctor"
      icon={<LocalHospital />}
      color={C.purple}
      onViewAll={() =>
        setDialog({ open: true, title: "All Visits by Doctor", rows: m.doctorTableRows, columns: doctorCols, csv: "doctor_visits.csv" })
      }
    >
      {m.doctorData.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>No data</Typography>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          <LeaderList data={m.doctorData} colors={docColors} />
        </Box>
      )}
    </SectionCard>
  </Grid2>

  <Grid2 size={{ xs: 12, md: 4 }}>
    <SectionCard
      title="Top Services"
      icon={<MedicalServices />}
      color={C.teal}
      onViewAll={() =>
        setDialog({ open: true, title: "All Visits by Service", rows: m.serviceTableRows, columns: serviceCols, csv: "service_visits.csv" })
      }
    >
      {m.serviceData.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>No data</Typography>
      ) : (
        <Box sx={{ mt: 1 }}>
          <LeaderList data={m.serviceData} colors={svcColors} />
        </Box>
      )}
    </SectionCard>
  </Grid2>

  <Grid2 size={{ xs: 12, md: 3 }}>
    <SectionCard
      title="Self vs Referral"
      icon={<Recommend />}
      color={C.coral}
      onViewAll={() => openVisitDlg("Referred Visits", m.referredV, "referred_visits.csv")}
    >
      <ChartBox id="vd-referral" height={120} label={`Self: ${m.selfV.length}, Referred: ${m.referredV.length}`} />
      <Box sx={{ display: "flex", justifyContent: "space-around", mt: 0.5 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: C.blue, lineHeight: 1 }}>{m.selfV.length}</Typography>
          <Typography variant="caption" color="text.secondary">Self</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: C.coral, lineHeight: 1 }}>{m.referredV.length}</Typography>
          <Typography variant="caption" color="text.secondary">Referred</Typography>
        </Box>
      </Box>
    </SectionCard>
  </Grid2>

</Grid2>

{/* ── Row 3b: Status Split | Closed Breakdown ── */}
<Grid2 container spacing={1.5} sx={{ mb: 2 }}>

  <Grid2 size={{ xs: 12, md: 6 }}>
    <SectionCard title="Status Split" icon={<MedicalInformation />} color={C.amber}>
      <Typography variant="caption" color="text.secondary"
        sx={{ fontSize: "0.6rem", display: "block", mb: 0.5, textAlign: "center" }}>
        Click segment to view visits
      </Typography>
      <ChartBox id="vd-status" height={200} label="Status distribution doughnut" />
      <Box sx={{ display: "flex", justifyContent: "space-around", mt: 1, flexWrap: "wrap", gap: 0.5 }}>
        {[
          { label: "Completed", value: m.completedV.length, color: C.green,  bgColor: C.greenL, rows: m.completedV, csv: "completed_visits.csv" },
          { label: "Open",      value: m.openV.length,      color: C.blue,   bgColor: C.blueL,  rows: m.openV,      csv: "open_visits.csv" },
          { label: "New",       value: m.newV.length,       color: C.teal,   bgColor: C.tealL,  rows: m.newV,       csv: "new_visits.csv" },
          { label: "Cancelled", value: m.cancelledV.length, color: C.coral,  bgColor: C.coralL, rows: m.cancelledV, csv: "cancelled_visits.csv" },
          { label: "No Show",   value: m.noShowV.length,    color: C.amber,  bgColor: C.amberL, rows: m.noShowV,    csv: "noshow_visits.csv" },
        ].map(({ label, value, color, bgColor, rows, csv }) => (
          <Box key={label}
            onClick={() => openVisitDlg(`${label} Visits`, rows, csv)}
            sx={{ textAlign: "center", cursor: "pointer", px: 1, py: 0.5, borderRadius: 1, "&:hover": { background: bgColor } }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1 }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </SectionCard>
  </Grid2>

  <Grid2 size={{ xs: 12, md: 6 }}>
    <SectionCard title="Closed Breakdown" icon={<CheckCircleOutline />} color={C.green}>
      <ChartBox id="vd-closed3" height={200} label="Completed Cancelled NoShow pie" />
      <Box sx={{ display: "flex", justifyContent: "space-around", mt: 1 }}>
        <Box sx={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => openVisitDlg("Completed Visits", m.completedV, "completed_visits.csv")}>
          <Typography variant="h6" fontWeight={700} sx={{ color: C.green, lineHeight: 1 }}>{m.completedV.length}</Typography>
          <Typography variant="caption" color="text.secondary">Completed</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => openVisitDlg("Cancelled Visits", m.cancelledV, "cancelled_visits.csv")}>
          <Typography variant="h6" fontWeight={700} sx={{ color: C.coral, lineHeight: 1 }}>{m.cancelledV.length}</Typography>
          <Typography variant="caption" color="text.secondary">Cancelled</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => openVisitDlg("No Show Visits", m.noShowV, "noshow_visits.csv")}>
          <Typography variant="h6" fontWeight={700} sx={{ color: C.amber, lineHeight: 1 }}>{m.noShowV.length}</Typography>
          <Typography variant="caption" color="text.secondary">No Show</Typography>
        </Box>
      </Box>
    </SectionCard>
  </Grid2>

</Grid2>

            {/* ── Row 4: Completed + Cancelled ── */}
            {/* <Grid2 container spacing={1.5}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <SectionCard
                  title="Completed Visits"
                  icon={<CheckCircleOutline />}
                  color={C.green}
                  onViewAll={() =>
                    openVisitDlg("Completed Visits", m.completedV, "completed_visits.csv")
                  }
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h3" fontWeight={700} sx={{ color: C.green }}>
                      {m.completedV.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      visits completed
                      <br />
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {m.total > 0
                          ? ((m.completedV.length / m.total) * 100).toFixed(1)
                          : 0}
                        %
                      </Box>{" "}
                      of total
                    </Typography>
                  </Box>
                </SectionCard>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                <SectionCard
                  title="Cancelled Visits"
                  icon={<Cancel />}
                  color={C.coral}
                  onViewAll={() =>
                    openVisitDlg("Cancelled Visits", m.cancelledV, "cancelled_visits.csv")
                  }
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h3" fontWeight={700} sx={{ color: C.coral }}>
                      {m.cancelledV.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      visits cancelled
                      <br />
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {m.total > 0
                          ? ((m.cancelledV.length / m.total) * 100).toFixed(1)
                          : 0}
                        %
                      </Box>{" "}
                      of total
                    </Typography>
                  </Box>
                </SectionCard>
              </Grid2>
            </Grid2> */}
          </>
        )}
      </Box>

      {/* Date range dialog */}
      <DateRangeDialog
        key="vd-date"
        title="Dashboard Date Range"
        filters={filters}
        setFilters={setFilters}
        openState={openDateDlg}
        setOpenState={setOpenDateDlg}
      />

      {/* Table dialog */}
      <TableDialog
        open={dialog.open}
        onClose={closeDialog}
        title={dialog.title}
        rows={dialog.rows}
        columns={dialog.columns}
        csvFilename={dialog.csv}
        auth={auth}
        filters={filters}
      />
    </Box>
  );
}
